import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/shared/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all duration-200 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-1 aria-invalid:ring-red-500/20 aria-invalid:border-red-500 cursor-pointer",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-white hover:bg-primary/90 active:scale-[0.98]",
        destructive:
          "bg-red-500 text-white hover:bg-red-600 active:scale-[0.98]",
        // outline / secondary / ghost — 다크/라이트 자동 추적 (tokens.css 변수)
        // outline 보더: var(--bs-line) — 카드(흰) 안에서도 또렷이 보이도록 진한 톤
        outline:
          "border border-[var(--bs-line)] bg-[var(--bs-paper)] text-[var(--bs-ink-soft)] hover:bg-[var(--bs-paper-alt)] active:scale-[0.98]",
        secondary:
          "bg-[var(--bs-paper-alt)] text-[var(--bs-ink)] hover:bg-[var(--bs-line)] active:scale-[0.98]",
        ghost:
          "text-[var(--bs-ink-soft)] hover:bg-[var(--bs-paper-alt)] hover:text-[var(--bs-ink)]",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-5 py-2 has-[>svg]:px-4",
        sm: "h-8 rounded-xl gap-1.5 px-3 text-xs has-[>svg]:px-2.5",
        lg: "h-11 rounded-xl px-7 text-base has-[>svg]:px-5",
        icon: "size-9 rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
